import { useEffect, useState, useRef, useMemo } from "react";

import Box from "@mui/material/Box";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { AgGridReact } from "ag-grid-react";
import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";

import salesPlanService from "@plannel/services/data/sales-plan-service";
import customerService from "@plannel/services/data/customer-service";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";

import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import GridDatePicker from "@plannel/components/GridDatePicker"
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import PaginationContainer from "@plannel/components/PaginationContainer";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear() + 1, CURRENT_DATE.getMonth(), 0);
const END_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 10, CURRENT_DATE.getMonth(), 0);
const initPeriod = { start: START_DATE, end: END_DATE }

const { snackBarMessageColor } = ColorUtil;
const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    const dateCopy = new Date(e);

    if (type) {
      if (type === "start") {
        dateCopy.setDate(1);
        dateCopy.setHours(0, 0, 0, 0);
      } else if (type === "end") {
        const year = dateCopy.getFullYear();
        const month = dateCopy.getMonth();
        const lastDay = new Date(year, month + 1, 0).getDate();

        dateCopy.setDate(lastDay);
        dateCopy.setHours(23, 59, 59, 999);
      }

      setPeriod({ ...period, [type]: dateCopy });
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} >
      <DatePicker
        label={t("periodStart")}
        views={['year', 'month']}
        format={'yyyy-MM'}
        value={period.start}
        maxDate={period.end ? period.end : END_MAX_DATE}
        minDate={START_MIN_DATE}
        onChange={(e) => onChange(e, "start")}
        slotProps={{ textField: { size: "small", sx: { mr: 1.5, width: 130 } } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={['year', 'month']}
        format={'yyyy-MM'}
        value={period.end}
        maxDate={END_MAX_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        onChange={(e) => onChange(e, "end")}
        slotProps={{ textField: { size: "small", sx: { mr: 1.5, width: 130 } } }}
      />
    </LocalizationProvider>
  )
}

const SalesPlanMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);
  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const custHrchySearch = useRef(reduxViewState?.custHrchySearch ?? "");
  const itemHrchySearch = useRef(reduxViewState?.itemHrchySearch ?? "");
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : initPeriod);
  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  
  const [salesPlan, setSalesPlan] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custHrchys, setCustHrchys] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    } 

    if (custHrchySearch.current) {
      searchFilters.custHrchyCd = custHrchySearch.current;
    }

    if (itemHrchySearch.current) {
      searchFilters.itemHrchyCd = itemHrchySearch.current;
    }

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const setGridData = (resultList) => {
    for (const result of resultList) {
      if (!result.item && result?.itemHrchy) {
        result.item = result?.itemHrchy;
        result.item.itemCd = result?.itemHrchy?.hrchyCd;
      } 
      if (!result.customer && result?.customerHrchy) {
        result.customer = result?.customerHrchy;
        result.customer.customerCd = result?.customerHrchy?.hrchyCd;
      }
    }
    return resultList;
  };

  const retrieveSalesPlan = () => {
    const params = getRequestParams();
    salesPlanService.getAll(params)
    .then(response => {
      const { results, totalPages, isLimit, limitSize } = response.data;
      if (isEmpty(results)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
      }
      setLimitInfo({ isLimit, limitSize });
      setSalesPlan(setGridData(results));
      setTotalPages(totalPages);
    })
    .catch(e => {
      debug(e);
    })
    .then(() => {
      const reduxState = reduxUtil.createObjectState(viewName, {itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, currentPage, pageSize, ...period, advancedFilters});
      reduxDispatch(reduxUtil.updateViewState(reduxState));
      DataState.initialize(gridRef.current?.api);
    });
  }

  const retrieveCustomers = () => {
    customerService.getLookup()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveCustHrchys = () => {
    customerHrchyService.getAll()
      .then(response => {
        const { results } = response.data;
        const hrchyList = results.map(result => {
          result.customerCd = result.hrchyCd;
          return result;
        });
        setCustHrchys(hrchyList);
      })
      .catch(e => {
        debug(e);
      });
  }

  const removeSalesPlan = () => {
    const salesPlanIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        salesPlanIds.push(data.id);
      }
    );

    if (salesPlanIds.length > 0) {
      salesPlanService.remove(salesPlanIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveSalesPlan();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const setNodeData = rowDatas => {
    return rowDatas.map(rowData => {
      rowData.itemNodeId = rowData?.item?.id;
      rowData.customerNodeId = rowData?.customer?.id;
      return rowData;
    });
  }; 

  const saveSalesPlan = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      const rowDatas = setNodeData(allStateData);
      salesPlanService.upsert(rowDatas)
        .then((response) => {
          if (response?.data?.message) {
            const message = response?.data?.message;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message)});
            return;
          }
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          retrieveSalesPlan();
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveSalesPlan();
    } else if (action === "DELETE") {
      removeSalesPlan();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSalesPlan();
    }
  }

  const addRow = () => {
    const newRow = {
      id: null,
      customerCd: "",
      locCd: "",
      activeFlg: true,
      itemHrchyFlg: false,
      custHrchyFlg: false
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const customerKeyCreator = (params) => {
    return params.value.customerCd;
  }

  const dropdownValueFormatter = (data) => {
    return  data?.value?.name ?? data?.name ?? "";
  }

  const customerFormatter = (params) => {
    const filterVal = customers.filter(arr => arr.customerCd === params.value);
    return filterVal.length > 0 ? filterVal[0].name : null;
  }
 
  const setItemCode = function (params) {
    params.data.item = { itemCd: params.newValue, name: "" };
    return true;
  }

  const cellCellEditorParams = params => {
    const hrchyFlg = "customerHrchyFlg";
    const hrchys = custHrchys;
    const bottomValues = customers;
    const editValues = isEqual(params?.data?.[hrchyFlg], true) ? hrchys : bottomValues;
    return {
      values: editValues,
      formatValue: dropdownValueFormatter,
    };
  };

  const columnDefs = [
    {
      cellClass: 'stringType',
      headerName: "itemNodeCd",
      field: "item.itemCd",
      colId: "itemNodeCd",
      valueSetter: setItemCode,
      minWidth: 180,
      flex: 2,
      filterType: 'string'
    },
    { headerName: "itemNodeNm", field: "item.name", colId: "itemNodeNm", type: ["nonEditableColumn"], minWidth: 200, flex: 5, filterType: 'string' },
    {
      headerName: "itemHrchyFlg",
      field: "itemHrchyFlg",
      width: 130,
      type: ["booleanColumn", "nonEditableColumn"],
      filterType: 'boolean'
    },
    {
      headerName: "customerNodeCd",
      field: "customer.customerCd",
      colId: "customerNodeCd",
      minWidth: 180,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: "stringType",
      filterType: 'string'
    },
    {
      headerName: "customerNodeNm",
      field: "customer",
      colId: "customerNodeNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      keyCreator: customerKeyCreator,
      cellEditorParams: cellCellEditorParams,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: customerFormatter,
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => {
        const val1 = !value1 ? "" : value1.name;
        const val2 = !value2 ? "" : value2.name;
        
        return nullSafeComparator(val1, val2, isDescending);
      },
      minWidth: 180,
      flex: 2,
      filterType: 'string'
    },
    {
      headerName: "customerHrchyFlg",
      field: "customerHrchyFlg",
      width: 100,
      type: ["booleanColumn"],
      filterType: 'boolean'
    },
    { headerName: "dueDate", field: "dueDate", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 100, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd") },
    { headerName: "salesPlanQty", field: "salesPlanQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "salesPlanAmt", field: "salesPlanAmt", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 170, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 170, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
  ];

  const onGridReady = params => {
    retrieveCustomers();
    retrieveCustHrchys();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveSalesPlan();
    }

    return () => {
      setSalesPlan([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setSalesPlan([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, period]);

  useEffect(() => {
    if (limitInfo?.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({viewName : `${viewName}_NOTIFY`, showLimitSnackbar: false}));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo?.isLimit, showLimitSnackbar.current]);

  useEffect(() => {
    if (!isEmpty(customers || !isEmpty(custHrchys))) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, custHrchys]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <PeriodFilter period={period} setPeriod={setPeriod} />
        <TextFieldSearch sx={{ mr: 1, width: 250 }} label={"itemHrchyCd"} searchCode={itemHrchySearch} retrieve={initPageToRetrieve}/>
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} sx={{ mr: 1, width: 250 }} extraCodesRef={extraItemCodesRef}/>
        <TextFieldSearch sx={{ mr: 1, width: 250 }} label={"customerHrchyCd"} searchCode={custHrchySearch} retrieve={initPageToRetrieve} />
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} useAccessFilter={false} sx={{ mr: 1, width: 250 }} extraCodesRef={extraCustomerCodesRef}/>
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={salesPlanService} gridApi={gridRef.current?.api} columnDefs={columnDefs}
            params={getRequestParams()} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={salesPlan}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              enterNavigatesVertically
              onGridReady={onGridReady}
              statusBar={statusBar}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(SalesPlanMaster);